import React, { ChangeEvent, useEffect, KeyboardEvent } from 'react';
import { useStyles } from './Location.styles';
import { Icon, Icons } from 'components/Global/Icon/Icon';
import { appPalette } from 'theme/App.palette';
import {
  ClickAwayListener,
  Collapse,
  Grid,
  IconButton,
  Input,
} from '@mui/material';
import { useState, useRef } from 'react';
import { useValidateZipcode } from 'hooks/useValidateZipcode';
import TextRender from '../TextRender/TextRender';
import { selectZipcode, setZipcode } from 'redux/slices/zipcodeSlice';
import { useDispatch, useSelector } from 'react-redux';
import { IDealerLocatorLabels } from 'interfaces/dealerLocator';

export interface ILocation {
  onChange: Function;
  labels: IDealerLocatorLabels;
  zipgateHeading?: string;
}

export function Location({
  onChange,
  labels,
  zipgateHeading,
}: ILocation): JSX.Element {
  const classes = useStyles();
  const dispatch = useDispatch();
  const zipcode = useSelector(selectZipcode);
  const { acura_red } = appPalette.colors;
  const { white } = appPalette.colors;
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [newZipcode, setNewZipcode] = useState('');
  const inputRef = useRef<HTMLInputElement>();
  const { result, error, isLoading, fetchData, coordinates } =
    useValidateZipcode(newZipcode, labels?.zipcodeError);

  const handleCloseForm = () => {
    setIsFormOpen(false);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleIsFormOpen = () => {
    if (!isFormOpen) {
      setIsFormOpen(true);
    } else {
      var event = new CustomEvent('react-zipgate-open', {
        detail: {
          pageHeading: zipgateHeading || '',
        },
      });
      window.dispatchEvent(event);
      setIsFormOpen(false);
    }
  };

  const handleFieldChange = (e: ChangeEvent) => {
    const input = inputRef.current;
    if (input) {
      const antiRegex = new RegExp(/[^0-9]+/g);
      const validZipcode = new RegExp(/^\d{5}$/);
      input.value = input.value.replace(antiRegex, '');

      if (input.value.match(validZipcode)) {
        setNewZipcode(input.value);
      } else {
        setNewZipcode('');
      }
    }
  };

  useEffect(() => {
    if (result && !error) {
      dispatch(setZipcode(newZipcode));
      if (coordinates) {
        onChange(coordinates);
      }
      if (isFormOpen) {
        setIsFormOpen(false);
        if (inputRef.current) {
          inputRef.current.value = '';
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, coordinates]);

  const handleNewZipcodeClick = () => {
    if (newZipcode.length > 0) {
      fetchData();
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && !isLoading && newZipcode.length > 0) {
      handleNewZipcodeClick();
    }
  };

  return (
    <ClickAwayListener onClickAway={handleCloseForm}>
      <Grid container direction="row" className={classes.root}>
        <Collapse in={isFormOpen}>
          <Grid container direction="row">
            <Input
              inputProps={{
                maxLength: 5,
                type: 'text',
                className: `acr-paragraph-9 ${error ? 'error' : ''}`,
                ref: inputRef,
              }}
              className={`acr-paragraph-9 ${classes.locationInput}`}
              placeholder={labels?.enterZip}
              onChange={handleFieldChange}
              disableUnderline={true}
              onKeyDown={handleKeyDown}
            />
            <IconButton
              disableRipple
              onClick={() => {
                !isLoading && newZipcode.length > 0 && handleNewZipcodeClick();
              }}
              className={`${classes.formButton} ${
                !isLoading && newZipcode.length > 0 ? 'is-enabled' : ''
              }`}
              size="large"
            >
              {isLoading && <span className={`${classes.spinner}`}></span>}
              <Icon type={Icons.arrowStrong} color={white} />
            </IconButton>
          </Grid>
        </Collapse>
        <Grid container direction="row">
          {error ? (
            <span className={`${classes.error} acr-paragraph-11`}>
              <TextRender text={error} />
            </span>
          ) : (
            <IconButton
              disableRipple
              onClick={handleIsFormOpen}
              className={`acr-paragraph-9 ${classes.locationButton} ${
                isFormOpen ? classes.isFormOpen : ''
              }`}
              size="large"
            >
              <Icon isDecorative type={Icons.compass} color={acura_red} />
              <TextRender
                text={
                  !isFormOpen
                    ? `${labels?.zipcodeLabel} ${zipcode || ''}`
                    : labels?.zipcodeCurrentLocation
                }
              />
            </IconButton>
          )}
        </Grid>
      </Grid>
    </ClickAwayListener>
  );
}
